import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep } from 'lodash';

import { CommonImageItemType, applyContainerImageItemToForm, createSystemImageFieldMappings } from '@/components-biz/ImageSelect';
import {
  applyImageStorageVolumesToForm,
  clearImageStorageVolumesFromForm,
  rebuildImageStorageVolumes,
} from '@/delivery-type-plugin/capabilities/maas-image-storage';
import { MAAS_INFERENCE_SOURCE_FIELD } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasFramework, MaasInferenceSource } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField, toFormJsonString } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import type { ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { INFERENCE_SERVICE_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import {
  resolveFrameworkFromInferenceImage,
  resolveInferenceSourceFromSystem,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import { syncBuiltinInferenceImageEchoFields } from '@/delivery-type-plugin/capabilities/maas-system-config/builtinImageEcho';
import {
  IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD,
  IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD,
} from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigFields';
import {
  applyImageContainerDeployConfigToForm,
  clearImageContainerDeployConfigFromForm,
} from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigForm';
import {
  parseInferenceFrameworkImageItem,
  resolvePresetInferenceImageItem,
} from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import { enableSkuModulesForPreset } from '@/delivery-type-plugin/platform/scene-layout';

interface InferenceFrameworkSnapshot {
  inferenceSource: MaasInferenceSource;
  system?: Record<string, unknown>;
  framework?: MaasFramework;
  inferenceFrameworkImage?: string;
  commonEnv?: ModelInstanceFormValues['commonEnv'];
  inferenceService?: unknown;
  imageContainerDeployInstanceScheduling?: ModelInstanceFormValues['_imageContainerDeployInstanceScheduling'];
  imageContainerDeployMountPath?: string;
}

interface UseInferenceFrameworkSettingOptions {
  form: FormInstance;
  systemField: string;
  systemDiskField: string;
  isEdit?: boolean;
  initValues?: ModelInstanceFormValues;
  onImageSelected?: (item: CommonImageItemType) => void;
  presetArchitecture?: string;
  presetImageLocked?: boolean;
  /** embedded 节点抽屉：镜像 CDC 服务写入字段 */
  serviceField?: string;
  /** embedded：CDC 全局调度字段写入父表单 */
  parentForm?: FormInstance;
  /** embedded：切换镜像时不重置全部节点组 */
  skipResetNodeGroups?: boolean;
  /** embedded：是否根据镜像预填/重置高级配置；训练集群传 false */
  prefillAdvancedConfig?: boolean;
  healthCheckField?: string;
  sharedMemoryField?: string;
}

/** 推理框架联动逻辑：负责来源切换、编辑快照恢复、镜像默认值写入。 */
export function useInferenceFrameworkSetting({
  form,
  systemField,
  systemDiskField: _systemDiskField,
  isEdit,
  initValues,
  onImageSelected,
  presetArchitecture,
  presetImageLocked,
  serviceField = INFERENCE_SERVICE_FIELD,
  parentForm,
  skipResetNodeGroups = false,
  prefillAdvancedConfig = true,
  healthCheckField = '_containerDeployHealthCheck',
  sharedMemoryField = '_containerDeploySharedMemory',
}: UseInferenceFrameworkSettingOptions) {
  const cdcTargetForm = parentForm ?? form;
  const inferenceSystemRaw = Form.useWatch(systemField, form);
  const inferenceFrameworkImageRaw = Form.useWatch('inferenceFrameworkImage', form);
  const inferenceSystem = parseFormJsonField<Record<string, unknown>>(inferenceSystemRaw);
  const imageMappings = useMemo(
    () => createSystemImageFieldMappings(systemField).filter((item) => !item.field.endsWith('.architecture')),
    [systemField],
  );

  const initialFrameworkSnapshot = useMemo<InferenceFrameworkSnapshot | undefined>(() => {
    if (!isEdit || !initValues) return undefined;
    const system = parseFormJsonField<Record<string, unknown>>(initValues._inferenceSystem);
    if (!system) return undefined;
    const imageUrl = String(system.image || system.url || system.imageKey || '').trim();
    if (!imageUrl) return undefined;
    return {
      inferenceSource: resolveInferenceSourceFromSystem(system),
      system: cloneDeep(system),
      framework: initValues.framework,
      inferenceFrameworkImage: initValues.inferenceFrameworkImage,
      commonEnv: initValues.commonEnv ? cloneDeep(initValues.commonEnv) : undefined,
      inferenceService: initValues._inferenceService != null ? cloneDeep(initValues._inferenceService as object) : undefined,
      imageContainerDeployInstanceScheduling: initValues._imageContainerDeployInstanceScheduling
        ? cloneDeep(initValues._imageContainerDeployInstanceScheduling)
        : undefined,
      imageContainerDeployMountPath: initValues._imageContainerDeployMountPath,
    };
  }, [initValues, isEdit]);

  const readInferenceSourceFromForm = useCallback((): MaasInferenceSource => {
    const saved = form.getFieldValue(MAAS_INFERENCE_SOURCE_FIELD) as MaasInferenceSource | undefined;
    if (saved === MaasInferenceSource.Builtin || saved === MaasInferenceSource.Custom) {
      return saved;
    }
    return resolveInferenceSourceFromSystem(parseFormJsonField<Record<string, unknown>>(form.getFieldValue(systemField)));
  }, [form, systemField]);

  const [inferenceSource, setInferenceSource] = useState<MaasInferenceSource>(() => readInferenceSourceFromForm());
  const sourceChangeLockRef = useRef(false);

  const presetImageItem = useMemo(() => {
    if (!presetImageLocked) return null;
    return resolvePresetInferenceImageItem({
      inferenceFrameworkImage: inferenceFrameworkImageRaw,
      _inferenceSystem: inferenceSystemRaw,
    });
  }, [inferenceFrameworkImageRaw, inferenceSystemRaw, presetImageLocked]);

  /** 编辑旧 preset 常缺 maasInferenceSource；须回写 Form 字段，否则确认校验会落到笼统「请填写必填项」 */
  useEffect(() => {
    if (presetImageLocked) return;
    if (sourceChangeLockRef.current) return;

    const saved = form.getFieldValue(MAAS_INFERENCE_SOURCE_FIELD) as MaasInferenceSource | undefined;
    if (saved === MaasInferenceSource.Builtin || saved === MaasInferenceSource.Custom) {
      setInferenceSource(saved);
      if (saved === MaasInferenceSource.Builtin && inferenceSystem) {
        syncBuiltinInferenceImageEchoFields(form, systemField, inferenceSystem, inferenceFrameworkImageRaw);
      }
      return;
    }

    const resolved = resolveInferenceSourceFromSystem(inferenceSystem);
    setInferenceSource(resolved);
    form.setFieldValue(MAAS_INFERENCE_SOURCE_FIELD, resolved);

    if (resolved === MaasInferenceSource.Builtin && inferenceSystem) {
      syncBuiltinInferenceImageEchoFields(form, systemField, inferenceSystem, inferenceFrameworkImageRaw);
    } else if (resolved === MaasInferenceSource.Custom && inferenceSystem) {
      const imageUrl = String(inferenceSystem.image || inferenceSystem.url || '').trim();
      if (imageUrl && !String(form.getFieldValue(`${systemField}.image`) ?? '').trim()) {
        form.setFieldValue(`${systemField}.image`, imageUrl);
      }
    }
  }, [form, inferenceFrameworkImageRaw, inferenceSystem, presetImageLocked, systemField]);

  const includeServicesFromImage = !skipResetNodeGroups;

  const handleSourceChange = useCallback(
    (source: MaasInferenceSource) => {
      if (presetImageLocked) return;
      sourceChangeLockRef.current = true;
      setInferenceSource(source);
      form.setFieldValue(MAAS_INFERENCE_SOURCE_FIELD, source);

      if (isEdit && initialFrameworkSnapshot && source === initialFrameworkSnapshot.inferenceSource && initialFrameworkSnapshot.system) {
        form.setFieldsValue({
          framework: initialFrameworkSnapshot.framework,
          inferenceFrameworkImage: initialFrameworkSnapshot.inferenceFrameworkImage,
          commonEnv: initialFrameworkSnapshot.commonEnv ? cloneDeep(initialFrameworkSnapshot.commonEnv) : undefined,
          [IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD]: initialFrameworkSnapshot.imageContainerDeployMountPath,
        });
        form.setFieldValue(systemField, toFormJsonString(cloneDeep(initialFrameworkSnapshot.system)));
        if (initialFrameworkSnapshot.inferenceService) {
          form.setFieldValue(INFERENCE_SERVICE_FIELD, toFormJsonString(cloneDeep(initialFrameworkSnapshot.inferenceService)));
        }
        if (initialFrameworkSnapshot.imageContainerDeployInstanceScheduling) {
          form.setFieldValue(
            IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD,
            toFormJsonString(cloneDeep(initialFrameworkSnapshot.imageContainerDeployInstanceScheduling)),
          );
        }
        const restoredImage = parseFormJsonField<CommonImageItemType>(initialFrameworkSnapshot.inferenceFrameworkImage);
        applyImageStorageVolumesToForm(form, restoredImage);
      } else {
        imageMappings.forEach(({ field }) => {
          form.setFieldValue(field, undefined);
        });
        form.setFieldsValue({
          [systemField]: undefined,
          [`${systemField}.imageKey`]: undefined,
          [`${systemField}.imageName`]: undefined,
          framework: undefined,
          inferenceFrameworkImage: undefined,
        });
        clearImageContainerDeployConfigFromForm(cdcTargetForm, {
          includeServices: includeServicesFromImage,
          prefillAdvancedConfig,
          healthCheckField,
          sharedMemoryField,
          healthCheckTargetForm: form,
          sharedMemoryTargetForm: form,
        });
        clearImageStorageVolumesFromForm(cdcTargetForm);
      }

      requestAnimationFrame(() => {
        sourceChangeLockRef.current = false;
      });
    },
    [
      cdcTargetForm,
      form,
      healthCheckField,
      imageMappings,
      includeServicesFromImage,
      initialFrameworkSnapshot,
      isEdit,
      prefillAdvancedConfig,
      presetImageLocked,
      sharedMemoryField,
      skipResetNodeGroups,
      systemField,
    ],
  );

  const clearInferenceImageFields = useCallback(() => {
    imageMappings.forEach(({ field }) => {
      form.setFieldValue(field, undefined);
    });
    form.setFieldsValue({
      [`${systemField}.imageKey`]: undefined,
      [`${systemField}.imageName`]: undefined,
      framework: undefined,
      inferenceFrameworkImage: undefined,
    });
    clearImageContainerDeployConfigFromForm(cdcTargetForm, {
      includeServices: includeServicesFromImage,
      prefillAdvancedConfig,
      healthCheckField,
      sharedMemoryField,
      healthCheckTargetForm: form,
      sharedMemoryTargetForm: form,
    });
    clearImageStorageVolumesFromForm(cdcTargetForm);
  }, [
    cdcTargetForm,
    form,
    healthCheckField,
    imageMappings,
    includeServicesFromImage,
    prefillAdvancedConfig,
    sharedMemoryField,
    skipResetNodeGroups,
    systemField,
  ]);

  const handleImageChange = useCallback(
    async (_key: string, item: CommonImageItemType, imageUrl?: string) => {
      if (presetImageLocked) return;
      // 切换镜像前先清理上一张镜像解析写入的字段，避免旧值残留。
      clearImageContainerDeployConfigFromForm(cdcTargetForm, {
        includeServices: includeServicesFromImage,
        prefillAdvancedConfig,
        healthCheckField,
        sharedMemoryField,
        healthCheckTargetForm: form,
        sharedMemoryTargetForm: form,
      });
      form.setFieldValue('inferenceFrameworkImage', JSON.stringify(item));
      form.setFieldValue(`${systemField}.imageKey`, String(item.id ?? item.key ?? ''));
      applyContainerImageItemToForm(form, item, imageMappings);
      const resolvedUrl = imageUrl ?? String(item.dockerRefUrl ?? '').trim();
      if (resolvedUrl) {
        form.setFieldValue(`${systemField}.image`, resolvedUrl);
      }

      applyImageContainerDeployConfigToForm(cdcTargetForm, item, {
        setModelMountPath: true,
        modelName: String((parentForm ?? form).getFieldValue('model.name') ?? '').trim(),
        includeStartupCommands: false,
        includeServices: includeServicesFromImage,
        serviceField,
        healthCheckField,
        sharedMemoryField,
        serviceTargetForm: form,
        healthCheckTargetForm: form,
        sharedMemoryTargetForm: form,
        prefillAdvancedConfig,
      });

      applyImageStorageVolumesToForm(form, item);
      if (rebuildImageStorageVolumes(item).length) {
        enableSkuModulesForPreset(form, ['storage']);
      }

      form.setFieldValue('framework', resolveFrameworkFromInferenceImage(item, form.getFieldValue(systemField)));
      onImageSelected?.(item);
    },
    [
      cdcTargetForm,
      form,
      healthCheckField,
      imageMappings,
      includeServicesFromImage,
      onImageSelected,
      parentForm,
      prefillAdvancedConfig,
      presetImageLocked,
      serviceField,
      sharedMemoryField,
      skipResetNodeGroups,
      systemField,
    ],
  );

  const handleImageClear = useCallback(() => {
    if (presetImageLocked) return;
    clearInferenceImageFields();
  }, [clearInferenceImageFields, presetImageLocked]);

  return {
    imageMappings,
    inferenceSource,
    handleSourceChange,
    handleImageChange,
    handleImageClear,
    presetImageItem,
  };
}
